import { Box, IconButton, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ExchangeRateDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { getAppSettings } from '../settings/redux/settingsSelectors';
import { CurrencyInput } from './CurrencyInput';
import { getExchangeRates } from './redux/exchangeRateSelectors';
import { distinct } from '../../utils';
import ImportExportIcon from '@mui/icons-material/ImportExport';

const mapStateToProps = (state: RootState) => ({
    exchangeRates: Object.values(getExchangeRates(state)),
    baseCurrency: getAppSettings(state)?.defaultCurrency
});

type Props = ReturnType<typeof mapStateToProps>;

type ExchangeRate = {
    amount?: number;
    currency?: string;
};

function findExchangeRate(
    exchangeRates: ExchangeRateDto[],
    fromCurrency: string | undefined,
    toCurrency: string | undefined,
    baseCurrency: string | undefined
) {
    if (!fromCurrency || !toCurrency) {
        return null;
    }

    if (fromCurrency === toCurrency) {
        return 1;
    }

    const exchangeRate = exchangeRates
        .filter(x => x.currency === toCurrency)
        .sort((a, b) => parseISO(b.validFrom).getTime() - parseISO(a.validFrom).getTime())[0]?.rate;

    if (fromCurrency === baseCurrency) {
        return exchangeRate;
    }

    const baseRate = exchangeRates
        .filter(x => x.currency === fromCurrency)
        .sort((a, b) => parseISO(b.validFrom).getTime() - parseISO(a.validFrom).getTime())[0]?.rate;

    if (toCurrency === baseCurrency) {
        return 1 / baseRate;
    }

    return (1 / baseRate) * exchangeRate;
}

const ExchangeRateCalculator = ({ exchangeRates, baseCurrency }: Props) => {
    const [from, setFrom] = useState<ExchangeRate>({});
    const [to, setTo] = useState<ExchangeRate>({});
    const availableCurrencies = exchangeRates.map(x => x.currency).filter(distinct);

    useEffect(() => {
        setFrom(x => ({ ...x, currency: baseCurrency }));
    }, [baseCurrency]);

    const handleSetFromAmount = (amount: number) => {
        setFrom(x => ({ ...x, amount }));

        const exchangeRate = findExchangeRate(exchangeRates, from.currency, to.currency, baseCurrency);

        if (exchangeRate != null) {
            setTo(x => ({ ...x, amount: exchangeRate * amount }));
        }
    };

    const handleSetFromCurrency = (currency: string) => {
        setFrom(x => ({ ...x, currency }));

        const exchangeRate = findExchangeRate(exchangeRates, currency, to.currency, baseCurrency);

        const amount = from.amount;
        if (exchangeRate != null && amount != null) {
            setTo(x => ({ ...x, amount: exchangeRate * amount }));
        }
    };

    const handleSetToAmount = (amount: number) => {
        setTo(x => ({ ...x, amount }));

        if (to.currency) {
            const exchangeRate = findExchangeRate(exchangeRates, from.currency, to.currency, baseCurrency);

            if (exchangeRate != null) {
                setFrom(x => ({ ...x, amount: amount / exchangeRate }));
            }
        }
    };

    const handleSetToCurrency = (currency: string) => {
        const exchangeRate = findExchangeRate(exchangeRates, from.currency, currency, baseCurrency);

        const amount = from.amount;
        setTo(x => ({
            currency,
            amount: exchangeRate != null && amount != null ? amount * exchangeRate : x.amount
        }));
    };

    if (exchangeRates.length === 0) {
        return null;
    }

    const handleSwitchCurrencies = () => {
        const fromCurrency = from.currency;
        const toCurrency = to.currency;

        setFrom(x => ({ ...x, currency: toCurrency }));

        const exchangeRate = findExchangeRate(exchangeRates, toCurrency, fromCurrency, baseCurrency);

        const fromAmount = from.amount;
        setTo(x => ({
            currency: fromCurrency,
            amount: exchangeRate != null && fromAmount != null ? fromAmount * exchangeRate : x.amount
        }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5" mb={2}>
                Currency converter
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <CurrencyInput
                        currencies={availableCurrencies}
                        amount={from.amount}
                        placeholder="From"
                        setAmount={handleSetFromAmount}
                        selectedCurrency={from.currency}
                        setSelectedCurrency={handleSetFromCurrency}
                    />
                    <CurrencyInput
                        currencies={availableCurrencies}
                        amount={to.amount}
                        placeholder="To"
                        setAmount={handleSetToAmount}
                        selectedCurrency={to.currency}
                        setSelectedCurrency={handleSetToCurrency}
                    />
                </Box>
                <IconButton color="primary" onClick={handleSwitchCurrencies}>
                    <ImportExportIcon />
                </IconButton>
            </Box>
        </Box>
    );
};
export default connect(mapStateToProps)(ExchangeRateCalculator);
